import DateFnsUtils from "@date-io/date-fns";
import { Button, Card, CardContent, FormControl, Grid, InputLabel, MenuItem, Select } from "@material-ui/core";
import {AccessTime, Sync, ExitToApp, Settings} from "@material-ui/icons";
import { MuiPickersUtilsProvider, KeyboardDatePicker, KeyboardTimePicker } from "@material-ui/pickers";
import { ChangeEvent, ReactNode, useState, useEffect } from "react";
import { isValid } from "date-fns";
import { IDataGetterCardProps } from "./props";
import "./data-getter-card.scss";
import * as AuthService from "../../../web-services/auth-service";
import { IUserData } from "../../../web-services/models/user-data";
import { UserRole } from "../../../utilities/UserRole";

/** Starts export and data acquiration process, with configurable input fields */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function DataGetterCard(props: IDataGetterCardProps)
{
    const [selectedTimeBreakdown, setSelectedTimeBreakdown] = useState(props.timeBreakdown);
    const [selectedFormat, setSelectedFormat] = useState(props.format);
    const [selectedStartDate, setSelectedStartDate] = useState(props.startDate);
    const [selectedEndDate, setSelectedEndDate] = useState(props.endDate);
    const [selectedStartTime, setSelectedStartTime] = useState(props.startTime);
    const [selectedEndTime, setSelectedEndTime] = useState(props.endTime);
    const [currentUser, setCurrentUser] = useState<IUserData | undefined>(undefined);
    let keyValue = 1;

    useEffect(() =>
    {
        const user = AuthService.getCurrentUser();

        if (user) {
            setCurrentUser(user);
        }
        setSelectedStartDate(props.startDate);
        setSelectedEndDate(props.endDate);
        setSelectedFormat(props.format);
        setSelectedTimeBreakdown(props.timeBreakdown);
        setSelectedStartTime(props.startTime);
        setSelectedEndTime(props.endTime);
    },[props.startDate, props.endDate, props.format, props.timeBreakdown, props.startTime, props.endTime]);

    const handleSelectedTimeBreakdownChange = (event: ChangeEvent<{
        name?: string | undefined;
        value: unknown;
    }>, child: ReactNode) =>
    {
        if (event.target.value === "ora" || event.target.value === "nap" || event.target.value === "nincs")
        {
            props.onTimeBreakdownChange?.(event.target.value);
        }
    };

    const handleSelectedFormatChange = (event: ChangeEvent<{
        name?: string | undefined;
        value: unknown;
    }>, child: ReactNode) =>
    {
        if (event.target.value === "csv" || event.target.value === "xlsx")
        {
            props.onFormatChange?.(event.target.value);
        }
    };

    const renderFormatField = () => (
        props.mode === "export"
            ? (
                <Grid item xs={12} md={4} lg={2}>
                    <FormControl variant="standard" className="wide-field">
                        <InputLabel>Formátum</InputLabel>
                        <Select
                            value={selectedFormat}
                            onChange={handleSelectedFormatChange}
                            label="Formátum"
                        >
                            <MenuItem value="csv">CSV</MenuItem>
                            <MenuItem value="xlsx">XLSX</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            )
            : undefined
    );

    const renderTimeBreakdownField = () => (
        props.mode === "graphicon"
            ? (
                <Grid item xs={12} md={4} lg={2}>
                    <FormControl variant="standard" className="wide-field">
                        <InputLabel>Lebontás</InputLabel>
                        <Select
                            value={selectedTimeBreakdown}
                            onChange={handleSelectedTimeBreakdownChange}
                            label="Lebontás"
                        >
                            <MenuItem value="ora">Óra</MenuItem>
                            <MenuItem value="nap">Nap</MenuItem>
                            <MenuItem value="nincs">Nincs</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            )
            : undefined
    );

    const hasVLERole = currentUser?.roles.some(role => role.name === UserRole.ROLE_VLE_MODIFIER);
    const renderModifierButton = () => (
        !props.mode && hasVLERole
        ? (
            <Button
                variant="contained"
                color="primary"
                startIcon={<Settings />}
                disabled={!props.modifiable}
                onClick={props.onModifyButtonClick}
            >
                Módosítom a szabad helyeket
            </Button>
        )
            : undefined
    );

    const handleStartDateChange = (date: any) => {
        const startDate = new Date(date);

        // Solution based of: https://stackoverflow.com/questions/43735678/typescript-get-difference-between-two-dates-in-days
        // https://stackoverflow.com/questions/3818193/how-to-add-number-of-days-to-todays-date
        if (!props.mode)
        {
            const diff = Math.abs(selectedEndDate.getTime() - startDate.getTime());
            const diffDays = Math.ceil(diff / (1000 * 3600 * 24));

            if (diffDays > 60)
            {
                const newEndDate = new Date(startDate.getTime() + (60 * 24 * 60 * 60 * 1000));

                props.onEndDateChange(newEndDate);
            }
        }

        if (startDate > selectedEndDate)
        {
            props.onStartDateChange(selectedEndDate);
        }
        else
        {
            props.onStartDateChange(startDate);
        }
    };


    const handleEndDateChange = (date: any) =>
    {
        const endDate = new Date(date);

        if (!props.mode || props.mode === "graphicon")
        {
            const diff = Math.abs(endDate.getTime() - selectedStartDate.getTime());
            const diffDays = Math.ceil(diff / (1000 * 3600 * 24));

            if (diffDays > 60)
            {
                const newStartDate = new Date(endDate.getTime() - (60 * 24 * 60 * 60 * 1000));

                props.onStartDateChange(newStartDate);
            }
        }

        if (endDate < selectedStartDate)
        {
            props.onEndDateChange(selectedStartDate);
        }
        else
        {
            props.onEndDateChange(endDate);
        }
    };

    const renderButton = () =>
    {
        const startIcon = !props.mode || props.mode === "graphicon" ? <Sync /> : <ExitToApp />;
        const buttonText = !props.mode || props.mode === "graphicon" ? "Lekérem" : "Exportálom";

        return (
            <Button
                variant="contained"
                color="primary"
                startIcon={startIcon}
                onClick={props.onButtonClick}
            >
                {buttonText}
            </Button>
        );
    };

    const handleStartTimeChange = (date: any) =>
    {
        // eslint-disable-next-line no-null/no-null
        if (date === null)
        {
            keyValue = 1;
            // eslint-disable-next-line no-null/no-null
            props.onStartTimeChange(null);
        }
        else
        {
            const startTime = new Date(date);

            if (!selectedEndTime || isValid(selectedEndTime) && startTime <= selectedEndTime)
            {
                props.onStartTimeChange(new Date(startTime.setSeconds(0)));
            }
            else
            {
                props.onStartTimeChange(selectedEndTime);
            }

            keyValue = 2;
        }
    };

    const handleEndTimeChange = (date: any) =>
    {
        // eslint-disable-next-line no-null/no-null
        if (date === null)
        {
            keyValue = 1;
            // eslint-disable-next-line no-null/no-null
            props.onEndTimeChange(null);
        }
        else
        {
            const endTime = new Date(date);
            if (!selectedStartTime || isValid(selectedStartTime) && endTime >= selectedStartTime)
            {
                props.onEndTimeChange(new Date(endTime.setSeconds(0)));
            }
            else
            {
                props.onEndTimeChange(selectedStartTime);
            }
            keyValue = 2;
        }

    };

    return (
        <Card>
            <CardContent>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={10}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4} lg={2}>
                                    <KeyboardDatePicker
                                        className="wide-field"
                                        label="Dátumtól"
                                        value={selectedStartDate}
                                        onChange={handleStartDateChange}
                                        format="yyyy.MM.dd"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4} lg={2}>
                                    <KeyboardDatePicker
                                        className="wide-field"
                                        label="Dátumig"
                                        value={selectedEndDate}
                                        onChange={handleEndDateChange}
                                        format="yyyy.MM.dd"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4} lg={2}>
                                    <KeyboardTimePicker
                                        className="wide-field"
                                        id="time-picker"
                                        label="Időszaktól"
                                        format="HH:mm:ss"
                                        // eslint-disable-next-line no-null/no-null
                                        value={selectedStartTime ? selectedStartTime : null}
                                        onChange={handleStartTimeChange}
                                        keyboardIcon={<AccessTime />}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4} lg={2}>
                                    <KeyboardTimePicker
                                        key={keyValue}
                                        className="wide-field"
                                        id="time-picker"
                                        label="Időszakig"
                                        format="HH:mm:ss"
                                        // eslint-disable-next-line no-null/no-null
                                        value={selectedEndTime ? selectedEndTime : null}
                                        onChange={handleEndTimeChange}
                                        keyboardIcon={<AccessTime />}
                                    />
                                </Grid>

                                {renderTimeBreakdownField()}
                                {renderFormatField()}
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={2} className="data-getter-button-item">
                            {renderModifierButton()}
                            {renderButton()}
                        </Grid>
                    </Grid>
                </MuiPickersUtilsProvider>
            </CardContent>
        </Card>
    );
}
